import {
  CardHeader,
  Button,
  Card,
  CardActions,
  CardContent,
  Modal,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useStore from "../../helpers/useStore";
import { UserDto } from "../../api-client";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import notificationStore from "../../stores/Notification/notificationStore";
import AbsenceDialog from "./AbsenceDialog";
import { ContinuousUserCalendarDialog } from "../../components/TerminPlan/ContinuousUserCalendarDialog";
import MapIcon from '@mui/icons-material/Map';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { useUserByIdWithTherapies } from "../../stores/User/user.queries";
import WarningBadge from "./WarningBadge";
import { mobiliTheme } from "../../themes/mobiliTheme";

const UserPopover = ({ resource, onConfirm, onRouteplanUserChange }: { 
  resource: any; 
  onConfirm: () => void;
  onRouteplanUserChange?: (user: UserDto) => void;
  }) => {
  const CalendarStore = useStore(CalendarContext);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [isContinuousCalendarModalOpen, setIsContinuousCalendarModalOpen] = useState<boolean>(false);

  const warningCount = resource.resource.extendedProps?.warningCount;
  const hasWarnings = warningCount !== undefined && warningCount > 0;

  const open = Boolean(anchorEl);
  const { data: userData, isLoading } = useUserByIdWithTherapies(
    open ? resource.resource.id : undefined,
    open
  );

  const handleAbsenceDialogOpen = () => setShowAbsenceDialog(true);

  const handleAbsenceDialogClose = (confirmed: boolean) => {
    setShowAbsenceDialog(false);
    if (confirmed) {
      // console.log("confirmed", confirmed);
      handleClose();
      onConfirm();
    }
  };

  const handleContinuousAppsClick = () => {
    setIsContinuousCalendarModalOpen(true);
  }

  const handleRouteplanClick = () => {
    handleClose();
    // Activate route planning mode and pass the selected user
    if (userData?.id) {
      CalendarStore.enableRoutePlanning(userData.id);
    }
    return onRouteplanUserChange && onRouteplanUserChange(userData as UserDto);
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const id = open ? "simple-popover" : undefined;
  return (
    <>
      <Button
        onClick={handleClick}
        variant="text"
        disableElevation
        sx={{
          background: "none",
          boxShadow: "none",
          color: mobiliTheme.mainHeader,
          textTransform: "none",
          fontWeight: 600,
          paddingTop: 0,
          paddingBottom: 0,
          borderRadius: 1,
          minWidth: "fit-content",
          textDecoration: resource.resource.extendedProps?.lastWorkDay ? "line-through 1.5px" : "none",
          textDecorationColor: resource.resource.extendedProps?.lastWorkDay ? "red" : "initial",

          // Borderless/transparent by default with a subtle clickable highlight on hover.
          "&:hover": {
            backgroundColor: "rgba(0, 48, 89, 0.08)",
            color: mobiliTheme.mainHeader,
          },
        }}
      >
        {resource.resource.title}
        {hasWarnings && <WarningBadge count={warningCount!} fontSize="14px" />}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Card sx={{ minWidth: 275, maxWidth: 450 }}>
          <CardHeader
            title={
              isLoading
                ? ".. wird geladen .."
                : userData
                ? `${userData.firstName} ${userData.lastName}`
                : "Unbekannter Benutzer"
            }
          />

          <CardContent>
            {/* <Typography sx={{ mb: 1.5 }} color="text.secondary">
              {resource.resource.id}
            </Typography> */}
            <List>
              <ListItem>
                <ListItemText primary="Handy" secondary={userData?.phone || "-"} />
              </ListItem>
              <Divider component="li" />
              <ListItem>
                <ListItemText primary="Adresse" secondary={userData?.address || "-"} />
              </ListItem>
              <Divider component="li" />
              <ListItem>
                <ListItemText
                  primary="Heilmittel"
                  secondary={userData?.therapies
                    ?.map((therapy: any) => therapy.abbreviation)
                    .join(", ")}
                />
              </ListItem>
              {/* Add more list items here if needed */}
            </List>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button size="small" variant="outlined" onClick={handleContinuousAppsClick} disabled startIcon={<AllInclusiveIcon/>}>
              Dauertermin
            </Button>
            <Button size="small" onClick={handleRouteplanClick} startIcon={<MapIcon/>}>
              Routenplan
            </Button>
            <Button size="small" onClick={handleAbsenceDialogOpen} startIcon={<DateRangeIcon/>}>
              Abwesenheit
            </Button>
          </CardActions>
        </Card>
      </Popover>
      <AbsenceDialog
        leaveOfAbsence={{
          user: { id: resource.resource.id },
          start: resource.date,
          loAType: "sickness"
        }}
        open={showAbsenceDialog}
        title="Therapeut wird krank gemeldet"
        onConfirm={onConfirm}
        onClose={() => handleAbsenceDialogClose(true)}
      />
       <ContinuousUserCalendarDialog open={isContinuousCalendarModalOpen} setOpen={setIsContinuousCalendarModalOpen} userId={resource.resource.id}/>
    </>
  );
};

const SickModal = ({
  userId,
  date,
  open,
  handleClose,
}: {
  userId: string;
  date: Date;
  open: boolean;
  handleClose: (confirmed: boolean) => void;
}) => {
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
  };

  const [isLoading, setIsLoading] = useState(false);

  const { userAbsence } = useStore(CalendarContext);

  const handleConfirmation = async () => {
    setIsLoading(true); // Start loading
    try {

      const leaveOfAbsence = {
        user: {id: userId},
        type: "sickness",
        from: date.toISOString(),
        to: date.toISOString(),
      };
      const canceledCount = await userAbsence(userId, leaveOfAbsence);

      if (canceledCount !== undefined) {
        // Handle the response (e.g., close the modal, display a message)
        // console.log("API call successful:", canceledCount);

        notificationStore.showMessage("" + canceledCount + " Termine wurden storniert", "success");
      }
      // throw Error("Test Error");
      handleClose(true);
    } catch (error) {
      console.error("There was a problem fetching user data:", error);
      notificationStore.showMessage("Fehler beim Stornieren der Termine", "error", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Card sx={style}>
        <CardHeader title="Therapeut wird krank gemeldet" />
        <CardContent>
          <Typography color="text.secondary" id="modal-modal-description" sx={{ mt: 2 }}>
            am {date.toLocaleDateString("de-DE")}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Sollten alle Termine an diesem Tag storniert werden?
          </Typography>
          <Typography color="text.secondary" id="modal-modal-description" sx={{ mt: 2 }}>
            Hier könnte man auch mehrerer Tage als Kranktage festlegen .... 
            {/* ({userId}) */}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <Button variant="text" onClick={() => handleClose(false)}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleConfirmation} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "Ja"}
          </Button>
        </CardActions>
      </Card>
    </Modal>
    
  );
};

export default UserPopover;
